import {
  apply,
  buildContent,
  createInitialState,
  getBreakthrough,
  getGenerator,
  getResource,
  gt,
  simulateOffline,
  tick,
  toNumber,
  ZERO,
  type Command,
  type Content,
  type GameEvent,
  type GameState,
} from '@core/index.js';
import { AGE_LOADERS, GlobalPackSchema, loadPack } from '@content/index.js';
import globalPack from '@content/packs/global.json';
import {
  DEFAULT_SETTINGS,
  SLOTS,
  encodeSave,
  exportSave,
  importSave,
  loadFromSlotWithMeta,
  loadSettings,
  requestPersistentStorage,
  saveSettings,
  saveToSlot,
  tryDecode,
  type Settings,
  type Slot,
} from '@persistence/index.js';
import { Interpolator, createRenderer } from '@render/index.js';
import {
  applyUiFrame,
  createOverlay,
  formatAmount,
  formatSignedAmount,
  showAwayModal,
  showSettingsModal,
  tagComponent,
  type AwayResourceGain,
  type OverlayIntent,
} from '@ui/index.js';
import { contentArtUrl, parallaxBackground, uiFrameArtUrl } from './contentArt.js';
import { startLoop } from './loop.js';
import { hardReloadApp, initPwa } from './pwa.js';
import { initVersionCheck } from './versionCheck.js';
import { lockViewport } from './viewport.js';
import { NON_PROD } from '../env.js';
import {
  buildOverlayViewModel,
  buildSceneViewModel,
  currentAge,
  primaryResourceId,
} from './viewModel.js';

/** Only surface the "while you were away" modal for a meaningful absence. */
const MIN_AWAY_MODAL_SEC = 60;

/** Arc I opens here; age progression beyond it arrives with the age-2 flow. */
const FIRST_AGE = 'age.primordial';

/** Achievements ship as flagged milestones in the global pack (GAM-25). */
const GLOBAL_PACK = GlobalPackSchema.parse(globalPack);
const ACHIEVEMENTS = GLOBAL_PACK.milestones.filter((m) => m.achievement === true);

function asSlot(value: string): Slot {
  return (SLOTS as readonly string[]).includes(value) ? (value as Slot) : SLOTS[0];
}

/** Load and validate the packs the session needs, flattened for the engine. */
async function loadContent(): Promise<Content> {
  const loader = AGE_LOADERS[FIRST_AGE];
  if (loader === undefined) {
    throw new Error(`no pack loader registered for ${FIRST_AGE}`);
  }
  const agePack = await loadPack(FIRST_AGE, loader);
  return buildContent([agePack], GLOBAL_PACK);
}

/**
 * The app bootstrap: wires every layer around the pure engine (GAM-9) plus the
 * Arc I release surface (GAM-25): settings/QoL, save management, achievements,
 * and the PWA update flow.
 *
 *   persistence → core (tick + offline catch-up over the Age 1 pack) ─┐
 *   content pack (validated JSON) ─────────────────────────────────────┼→ selector-derived view models
 *                                                                       ├→ DOM overlay (intents → engine commands)
 *                                                                       └→ Pixi scene + interpolated display
 *
 * Logic advances at the fixed 10 tps timestep; the number interpolator eases the
 * displayed primary counter every animation frame so it reads smoothly at 60 fps
 * (tech-design §4). The overlay never mutates state — it dispatches intents the
 * app maps onto `apply(state, command, content)`.
 */
async function main(): Promise<void> {
  const background = document.querySelector<HTMLElement>('#background');
  const overlayRoot = document.querySelector<HTMLElement>('#overlay');
  const topbarRoot = document.querySelector<HTMLElement>('#topbar');
  if (!background || !overlayRoot || !topbarRoot) {
    throw new Error('Missing #background / #topbar / #overlay mount points');
  }

  // Pin the viewport to 1:1 on touch devices (no pinch/double-tap zoom).
  lockViewport();

  const content = await loadContent();
  const renderer = await createRenderer(background);
  // The world-view backdrop is the age's generated renders — the scene render
  // plus the pack's parallax background (missing in local dev; the scene keeps
  // its procedural look until the assets load).
  const age1 = content.ages.get(FIRST_AGE);
  tagComponent(background, 'world-view (#background)', age1?.id);
  renderer.setScene('placeholder', {
    backdropUrl: contentArtUrl(FIRST_AGE),
    background: age1 === undefined ? undefined : parallaxBackground(age1),
  });
  // The age's 9-slice frame skins the DOM panels once its sheet loads (no-op in
  // dev builds without /content/ art).
  applyUiFrame(uiFrameArtUrl(FIRST_AGE));

  // Smooths the big primary counter between 10 tps logic updates.
  const primary = new Interpolator(0);

  let settings: Settings = await loadSettings();

  // A save from before content landed (or a fresh start) has no age selected —
  // enter Age 1 through the same command path the age picker will use later.
  const ensureAgeSelected = (s: GameState): GameState =>
    s.ages.current === null
      ? apply(s, { type: 'selectAge', id: FIRST_AGE }, content).state
      : s;

  // Load persisted state (or a fresh start) from the active slot. The stamp lets
  // us catch up on any time that passed while the tab was closed (tech-design §2.4).
  const loaded = await loadFromSlotWithMeta(asSlot(settings.activeSlot));
  let state: GameState = ensureAgeSelected(loaded.state);

  // Narration: Glorp's line the first time an entity is bought/activated.
  // Detected the same way as milestone toasts — a diff against a "seen" set,
  // not a GameEvent — but keyed on the current value crossing zero rather than
  // Set membership, since generators/breakthroughs/resources are numeric.
  const narrated = new Set<string>();
  // Seeded from a save as loaded, so its existing progress never re-narrates
  // but any new first (including offline-gained ones) still does. Re-run on
  // slot switch / import, which swap `state` out for an unrelated save.
  const seedNarrated = (s: GameState): void => {
    narrated.clear();
    for (const id of content.generators.keys()) {
      if (getGenerator(s, id).owned > 0) narrated.add(id);
    }
    for (const id of s.upgrades) narrated.add(id);
    for (const id of s.traits) narrated.add(id);
    for (const id of content.breakthroughs.keys()) {
      if (getBreakthrough(s, id).hits > 0) narrated.add(id);
    }
    for (const id of content.resources.keys()) {
      if (gt(getResource(s, id).lifetime, ZERO)) narrated.add(id);
    }
  };
  seedNarrated(state);
  const narrateFirstTimes = (s: GameState): void => {
    for (const [id, def] of content.generators) {
      if (!narrated.has(id) && getGenerator(s, id).owned > 0) {
        narrated.add(id);
        if (def.narration !== undefined) overlay.narrate(def.narration);
      }
    }
    for (const id of s.upgrades) {
      if (!narrated.has(id)) {
        narrated.add(id);
        const line = content.upgrades.get(id)?.narration;
        if (line !== undefined) overlay.narrate(line);
      }
    }
    for (const id of s.traits) {
      if (!narrated.has(id)) {
        narrated.add(id);
        const line = content.traits.get(id)?.narration;
        if (line !== undefined) overlay.narrate(line);
      }
    }
    for (const [id, def] of content.breakthroughs) {
      if (!narrated.has(id) && getBreakthrough(s, id).hits > 0) {
        narrated.add(id);
        if (def.narration !== undefined) overlay.narrate(def.narration);
      }
    }
    for (const [id, def] of content.resources) {
      if (!narrated.has(id) && gt(getResource(s, id).lifetime, ZERO)) {
        narrated.add(id);
        if (def.narration !== undefined) overlay.narrate(def.narration);
      }
    }
  };

  const primaryAmount = (): number => {
    const age = currentAge(state, content);
    const id = age === undefined ? undefined : primaryResourceId(age);
    return id === undefined ? 0 : toNumber(getResource(state, id).amount);
  };

  const render = (): void => {
    overlay.render(
      buildOverlayViewModel(state, content, {
        notation: settings.notation,
        displayPrimary: primary.value,
      }),
    );
    renderer.update(buildSceneViewModel(state, content));
  };

  // Non-gameplay preferences take effect immediately and on every change.
  const applySettings = (): void => {
    renderer.setParticleTier(settings.particleQuality);
    document.body.classList.toggle('reduced-motion', settings.reducedMotion);
    document.body.classList.toggle('high-contrast', settings.highContrast);
    resetAutosave();
  };

  // Milestone toasts: milestones the engine marks reached get surfaced once,
  // whether they landed via a command or mid-tick. Pack milestones carry their
  // own toast line; global achievements fall back to theirs.
  const toastedMilestones = new Set(state.milestones);
  const toastNewMilestones = (): void => {
    for (const id of state.milestones) {
      if (toastedMilestones.has(id)) continue;
      toastedMilestones.add(id);
      const def = content.milestones.get(id);
      overlay.toast(def?.toast ?? `Milestone reached — ${def?.name ?? id}`);
    }
  };

  /** Turn engine events into render/ui juice (the sim never depends on these). */
  const handleEvents = (events: readonly GameEvent[]): void => {
    for (const event of events) {
      switch (event.type) {
        case 'clicked':
          renderer.emit({ type: 'click' });
          break;
        case 'bought':
          renderer.emit({ type: 'purchase' });
          break;
        case 'breakthroughAttempt':
          if (!event.hit) {
            overlay.toast('No breakthrough — pity rises.');
          }
          break;
        case 'breakthroughHit': {
          renderer.emit({ type: 'breakthroughHit' });
          const names = event.traits.map((id) => content.traits.get(id)?.name ?? id);
          overlay.toast(
            names.length > 0
              ? `Breakthrough! Gained: ${names.join(', ')}.`
              : `Breakthrough! ${content.breakthroughs.get(event.id)?.name ?? event.id} succeeds.`,
          );
          break;
        }
        case 'ageCompleted': {
          const name = content.ages.get(event.age)?.name ?? event.age;
          overlay.toast(`Age complete — ${name}! The next age awaits.`);
          break;
        }
        case 'milestoneCompleted': // covered by the toastNewMilestones diff-scan
        case 'ageSelected':
        case 'prestiged':
          break;
      }
    }
    toastNewMilestones();
  };

  const toCommand = (intent: OverlayIntent): Command | null => {
    switch (intent.type) {
      case 'click': {
        const age = currentAge(state, content);
        return age === undefined ? null : { type: 'click', actionId: age.clickAction.id };
      }
      case 'buyGenerator':
        return { type: 'buyGenerator', id: intent.id, count: intent.count };
      case 'buyUpgrade':
        return { type: 'buyUpgrade', id: intent.id };
      case 'attemptBreakthrough':
        return { type: 'attemptBreakthrough', id: intent.id };
      case 'openSettings':
        return null;
    }
  };

  // The overlay never mutates state — it dispatches intents the app applies.
  const dispatch = (intent: OverlayIntent): void => {
    if (intent.type === 'openSettings') {
      openSettings();
      return;
    }
    const command = toCommand(intent);
    if (command === null) return;
    const result = apply(state, command, content);
    if (result.rejected !== null) return; // buttons disable, so rejects are benign races
    state = result.state;
    handleEvents(result.events);
    narrateFirstTimes(state);
    primary.setTarget(primaryAmount());
    render();
  };

  const overlay = createOverlay(overlayRoot, dispatch, topbarRoot);

  // Tapping the world view is the click/absorb action — replaces a dedicated
  // button, which was eating into the panel's limited vertical space. #overlay
  // sits above the canvas but is pointer-events:none except where a control
  // opts back in (the panel, toasts, narration), so clicks over open canvas
  // reach this listener untouched.
  if (age1 !== undefined) {
    const canvasHint = document.createElement('div');
    canvasHint.className = 'canvas-hint';
    const grantText = age1.clickAction.grants
      .map((grant) => {
        const name = content.resources.get(grant.resource)?.name ?? grant.resource;
        return `${formatSignedAmount(grant.amount, settings.notation)} ${name}`;
      })
      .join(', ');
    canvasHint.textContent = `${age1.clickAction.name} (${grantText})`;
    canvasHint.dataset.testid = 'canvas-hint';
    tagComponent(canvasHint, 'canvas-hint', age1.clickAction.id);
    background.append(canvasHint);
  }
  background.addEventListener('click', () => {
    dispatch({ type: 'click' });
  });

  // Offline catch-up: advance the sim by the elapsed absence through the same
  // tick() path over the real pack, then welcome the player back.
  if (loaded.savedAt !== null) {
    const elapsedSec = (Date.now() - loaded.savedAt) / 1000;
    const away = simulateOffline(state, content, elapsedSec);
    state = away.state;
    narrateFirstTimes(state);
    if (away.elapsedSec >= MIN_AWAY_MODAL_SEC) {
      const gains: AwayResourceGain[] = Object.entries(away.resourceDelta)
        .filter(([, delta]) => gt(delta, ZERO))
        .sort(([, a], [, b]) => toNumber(b) - toNumber(a))
        .map(([id, delta]) => ({
          label: content.resources.get(id)?.name ?? id,
          amount: formatAmount(delta, settings.notation),
        }));
      showAwayModal(overlayRoot, {
        elapsedSec: away.elapsedSec,
        capped: away.capped,
        gains,
        unlocks: [...away.milestones],
        ticks: away.ticks,
      });
    }
  }

  primary.snap(primaryAmount());
  render();

  startLoop(
    (dtTicks) => {
      state = tick(state, content, dtTicks);
      primary.setTarget(primaryAmount());
      toastNewMilestones();
      narrateFirstTimes(state);
    },
    (dtSeconds) => {
      primary.advance(dtSeconds);
      render();
    },
  );

  // Ask for durable storage once, the first time we write real progress.
  let persistenceRequested = false;
  const persist = async (): Promise<void> => {
    await saveToSlot(state, Date.now(), asSlot(settings.activeSlot));
    if (!persistenceRequested) {
      persistenceRequested = true;
      void requestPersistentStorage();
    }
  };

  // Periodic autosave (cadence is a setting) plus the reliable mobile lifecycle
  // signals (tech-design §5).
  let autosaveHandle = 0;
  const resetAutosave = (): void => {
    window.clearInterval(autosaveHandle);
    autosaveHandle = window.setInterval(
      () => void persist(),
      settings.autosaveInterval * 1000,
    );
  };
  window.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      void persist();
    }
  });
  window.addEventListener('pagehide', () => void persist());

  const openSettings = (): void => {
    const modal = showSettingsModal(
      overlayRoot,
      { ...settings, slots: SLOTS },
      ACHIEVEMENTS.map((a) => ({
        id: a.id,
        name: a.name ?? a.id,
        description: a.description ?? '',
        unlocked: state.milestones.includes(a.id),
      })),
      {
        update: (change) => {
          void saveSettings(change as Partial<Settings>).then((next) => {
            settings = next;
            applySettings();
            render();
          });
        },
        switchSlot: async (slot) => {
          await persist();
          settings = await saveSettings({ activeSlot: asSlot(slot) });
          const switched = await loadFromSlotWithMeta(asSlot(slot));
          state = ensureAgeSelected(switched.state);
          seedNarrated(state);
          primary.snap(primaryAmount());
          overlay.toast(`Now playing ${slot.replace('slot-', 'Slot ')}.`);
          render();
        },
        exportSave: () => exportSave(encodeSave(state, Date.now())),
        importSave: async (code) => {
          try {
            const imported = tryDecode(await importSave(code));
            if (!imported) return false;
            state = ensureAgeSelected(imported);
            seedNarrated(state);
            await persist();
            primary.snap(primaryAmount());
            render();
            return true;
          } catch {
            return false;
          }
        },
        resetSave: async () => {
          state = ensureAgeSelected(createInitialState());
          seedNarrated(state);
          await persist();
          primary.snap(primaryAmount());
          overlay.toast(`${settings.activeSlot.replace('slot-', 'Slot ')} reset.`);
          render();
        },
        resetSettings: () => {
          void saveSettings({
            ...DEFAULT_SETTINGS,
            activeSlot: settings.activeSlot,
          }).then((next) => {
            settings = next;
            applySettings();
            render();
            // The dialog's controls are a static snapshot taken at open time —
            // reopen so they reflect the restored defaults.
            modal.close();
            openSettings();
          });
        },
        hardReload: async () => {
          await persist();
          await hardReloadApp();
        },
      },
    );
  };

  applySettings();

  // PWA: prompt-strategy update flow — never clobber an unsaved session.
  initPwa({
    saveNow: persist,
    notify: (message) => {
      overlay.toast(message);
    },
    onUpdateReady: (applyUpdate) => {
      // Dev/preview: nobody's mid-session progress is at risk, so just take
      // the new build immediately instead of waiting on a click.
      if (NON_PROD) {
        void applyUpdate();
        return;
      }
      const banner = document.createElement('button');
      banner.type = 'button';
      banner.className = 'primary update-banner';
      banner.dataset.testid = 'update-banner';
      banner.textContent = 'Update available — save & reload';
      banner.addEventListener('click', () => void applyUpdate());
      overlayRoot.append(banner);
    },
  });

  // Auto-update: poll the deploy's version.json and hard-reload on a new build.
  initVersionCheck({ saveNow: persist });
}

void main();
